import { useState } from 'react'
import { Archive, Building2, Check, Pencil, Plus, Sparkles, X } from 'lucide-react'
import {
  Alert, Badge, Button, Card, EmptyState, IconButton, Input, Spinner, Tooltip,
} from '@/components/ui'
import { PERMISSIONS, usePermissions } from '@/features/authorization'
import { formatDayLabel, formatNumber } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import type { Patron } from '@/types'
import {
  useAddPatronDomain, usePatronSuggestions, usePatrons, useCreatePatron,
  useRemovePatronDomain, useUpdatePatron,
} from './hooks'

/**
 * Manage the patron list.
 *
 * The SUGGESTIONS block is the whole onboarding story and is deliberately at
 * the top: a workspace with two years of meetings already knows who it meets
 * with, and promoting a domain retroactively claims every past meeting because
 * membership is a read-time join rather than a stored link. Making somebody
 * type that list out by hand would be asking them for data we already hold.
 */
export function PatronsSettings() {
  const t = useT()
  const { data: patrons, isPending } = usePatrons(true)
  const { data: suggestions } = usePatronSuggestions(true)
  const create = useCreatePatron()
  const [newName, setNewName] = useState('')

  const unclaimed = suggestions ?? []

  return (
    <div className="space-y-4">
      <Alert tone="info" title={t('patrons.aboutTitle')}>
        {t('patrons.aboutBodyPrefix')}
        <strong>{t('patrons.aboutAutomatically')}</strong>
        {t('patrons.aboutBodyMid')}
        <em>{t('patrons.aboutAbout')}</em>
        {t('patrons.aboutBodySuffix')}
      </Alert>

      {unclaimed.length > 0 && (
        <Card>
          <div className="border-b border-border px-4 py-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-fg">
              <Sparkles aria-hidden className="h-4 w-4 text-brand" />
              {t('patrons.alreadyMeetWith')}
            </h3>
            <p className="mt-0.5 text-sm text-fg-muted">
              {t('patrons.alreadyMeetWithDesc')}
            </p>
          </div>
          <ul className="divide-y divide-border">
            {unclaimed.map((suggestion) => (
              <li
                key={suggestion.domain}
                className="flex items-center gap-3 px-4 py-2.5"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-mono text-sm text-fg">
                    {suggestion.domain}
                  </span>
                  <span className="block text-xs text-fg-subtle">
                    {suggestion.meetingCount === 1
                      ? t('patrons.oneMeeting', { count: formatNumber(suggestion.meetingCount) })
                      : t('patrons.manyMeetings', { count: formatNumber(suggestion.meetingCount) })}
                    {suggestion.lastMetAt && t('patrons.lastMet', { date: formatDayLabel(suggestion.lastMetAt) })}
                    {suggestion.exampleName && t('patrons.example', { name: suggestion.exampleName })}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={create.isPending}
                  onClick={() =>
                    create.mutate({
                      // The domain's second-level label is a decent first guess
                      // at the company name and is trivially editable; asking
                      // for it before showing any value is friction for nothing.
                      name: titleCase(suggestion.domain),
                      domains: [suggestion.domain],
                    })
                  }
                >
                  {t('patrons.addAsPatron')}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-2 border-b border-border px-4 py-3">
          <div className="min-w-[14rem] flex-1">
            <Input
              label={t('patrons.addAPatron')}
              placeholder="Northwind"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && newName.trim()) {
                  create.mutate({ name: newName.trim() }, { onSuccess: () => setNewName('') })
                }
              }}
            />
          </div>
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={!newName.trim() || create.isPending}
            onClick={() =>
              create.mutate({ name: newName.trim() }, { onSuccess: () => setNewName('') })
            }
          >
            {t('common.add')}
          </Button>
        </div>

        {isPending && (
          <p className="flex items-center gap-2 px-4 py-6 text-sm text-fg-muted">
            <Spinner /> {t('patrons.loading')}
          </p>
        )}

        {!isPending && (patrons ?? []).length === 0 && (
          <EmptyState
            icon={<Building2 />}
            title={t('patrons.noPatronsTitle')}
            description={t('patrons.noPatronsBody')}
          />
        )}

        <ul className="divide-y divide-border">
          {(patrons ?? []).map((patron) => (
            <PatronRow key={patron.id} patron={patron} />
          ))}
        </ul>
      </Card>
    </div>
  )
}

function PatronRow({ patron }: { patron: Patron }) {
  const t = useT()
  const { has } = usePermissions()
  const canManage = has(PERMISSIONS.patronManage)
  const addDomain = useAddPatronDomain()
  const removeDomain = useRemovePatronDomain()
  const update = useUpdatePatron()
  const [domain, setDomain] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)
  const [nameDraft, setNameDraft] = useState(patron.name)

  const submit = () => {
    const value = domain.trim()
    if (!value) return
    addDomain.mutate({ id: patron.id, domain: value }, { onSuccess: () => setDomain('') })
  }

  const startRename = () => {
    setNameDraft(patron.name)
    setIsRenaming(true)
  }

  const cancelRename = () => setIsRenaming(false)

  const saveRename = () => {
    const value = nameDraft.trim()
    if (!value || value === patron.name) {
      setIsRenaming(false)
      return
    }
    update.mutate(
      { id: patron.id, patch: { name: value } },
      { onSuccess: () => setIsRenaming(false) },
    )
  }

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Building2 aria-hidden className="h-4 w-4 text-fg-subtle" />
        {isRenaming ? (
          <span className="flex items-center gap-1.5">
            <Input
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') saveRename()
                if (event.key === 'Escape') cancelRename()
              }}
              aria-label={t('patrons.renameAria', { name: patron.name })}
              autoFocus
              className="h-8 w-48"
            />
            <IconButton
              label={t('common.save')}
              icon={<Check className="h-3.5 w-3.5" />}
              size="sm"
              disabled={!nameDraft.trim() || update.isPending}
              onClick={saveRename}
            />
            <IconButton
              label={t('common.cancel')}
              icon={<X className="h-3.5 w-3.5" />}
              size="sm"
              disabled={update.isPending}
              onClick={cancelRename}
            />
          </span>
        ) : (
          <span className="flex items-center gap-1">
            <span className="font-medium text-fg">{patron.name}</span>
            {canManage && (
              <IconButton
                label={t('patrons.renameTip')}
                icon={<Pencil className="h-3.5 w-3.5" />}
                size="sm"
                variant="ghost"
                onClick={startRename}
              />
            )}
          </span>
        )}
        {patron.status === 'archived' && (
          <Badge tone="neutral" size="sm">
            {t('common.archived')}
          </Badge>
        )}
        <span className="text-sm text-fg-subtle">
          {patron.meetingCount === 1
            ? t('patrons.oneMeeting', { count: formatNumber(patron.meetingCount) })
            : t('patrons.manyMeetings', { count: formatNumber(patron.meetingCount) })}
          {patron.openActionCount > 0 && t('patrons.openCount', { count: patron.openActionCount })}
        </span>
        <span className="flex-1" />
        <Tooltip
          content={
            patron.status === 'archived'
              ? t('patrons.restoreTip')
              : t('patrons.archiveTip')
          }
        >
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Archive className="h-3.5 w-3.5" />}
            onClick={() =>
              update.mutate({
                id: patron.id,
                patch: { status: patron.status === 'archived' ? 'active' : 'archived' },
              })
            }
          >
            {patron.status === 'archived' ? t('common.restore') : t('common.archive')}
          </Button>
        </Tooltip>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {patron.domains.map((value) => (
          <span
            key={value}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-raised px-2 py-0.5 font-mono text-xs text-fg"
          >
            {value}
            <button
              type="button"
              aria-label={t('patrons.removeDomain', { domain: value })}
              onClick={() => removeDomain.mutate({ id: patron.id, domain: value })}
              className="rounded-full p-0.5 hover:bg-surface"
            >
              <X aria-hidden className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={domain}
          onChange={(event) => setDomain(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit()
          }}
          onBlur={submit}
          placeholder={t('patrons.addDomainPlaceholder')}
          aria-label={t('patrons.addDomainAria', { name: patron.name })}
          className="min-w-[16rem] flex-1 rounded-md border border-dashed border-border bg-transparent px-2 py-0.5 text-xs text-fg placeholder:text-fg-subtle focus:border-brand focus:outline-none"
        />
      </div>
      {patron.domains.length === 0 && (
        <p className="mt-1 text-xs text-fg-subtle">
          {t('patrons.noDomainsYet')}
        </p>
      )}
    </li>
  )
}

/** "northwind.example" -> "Northwind". A guess, and editable. */
function titleCase(domain: string) {
  const label = domain.split('.')[0] ?? domain
  return label.charAt(0).toUpperCase() + label.slice(1)
}
